import './styles.css';
import { Game } from './game/Game';

type WebkitFullscreenDocument = Document & {
  webkitExitFullscreen?: () => Promise<void> | void;
  webkitFullscreenElement?: Element | null;
};

type WebkitFullscreenElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
};

function setupFullscreenToggle(): () => void {
  const button = document.querySelector<HTMLButtonElement>('#fullscreen-toggle');
  const label = document.querySelector<HTMLElement>('#fullscreen-toggle-label');
  const fullscreenDocument = document as WebkitFullscreenDocument;
  const fullscreenRoot = document.documentElement as WebkitFullscreenElement;

  if (!button || !label) return () => undefined;

  const getFullscreenElement = (): Element | null =>
    document.fullscreenElement ?? fullscreenDocument.webkitFullscreenElement ?? null;

  const updateButton = (): void => {
    const isFullscreen = Boolean(getFullscreenElement());
    const actionLabel = isFullscreen ? '전체화면 나가기' : '전체화면으로 보기';
    button.dataset.fullscreen = isFullscreen ? 'true' : 'false';
    button.setAttribute('aria-pressed', String(isFullscreen));
    button.setAttribute('aria-label', actionLabel);
    button.title = actionLabel;
    label.textContent = isFullscreen ? '나가기' : '전체화면';
  };

  const isSupported = Boolean(
    document.fullscreenEnabled ||
    fullscreenRoot.requestFullscreen ||
    fullscreenRoot.webkitRequestFullscreen,
  );

  if (!isSupported) {
    button.disabled = true;
    button.setAttribute('aria-label', '이 브라우저에서는 전체화면을 지원하지 않아요');
    button.title = '이 브라우저에서는 전체화면을 지원하지 않아요';
  }

  const toggleFullscreen = async (): Promise<void> => {
    if (!isSupported) return;
    try {
      if (getFullscreenElement()) {
        if (document.exitFullscreen) await document.exitFullscreen();
        else await fullscreenDocument.webkitExitFullscreen?.();
      } else if (fullscreenRoot.requestFullscreen) {
        await fullscreenRoot.requestFullscreen({ navigationUI: 'hide' });
      } else {
        await fullscreenRoot.webkitRequestFullscreen?.();
      }
    } catch {
      button.dataset.error = 'true';
      label.textContent = '다시 시도';
      window.setTimeout(() => {
        delete button.dataset.error;
        updateButton();
      }, 1600);
    }
  };

  button.addEventListener('click', toggleFullscreen);
  document.addEventListener('fullscreenchange', updateButton);
  document.addEventListener('webkitfullscreenchange', updateButton);
  updateButton();

  return () => {
    button.removeEventListener('click', toggleFullscreen);
    document.removeEventListener('fullscreenchange', updateButton);
    document.removeEventListener('webkitfullscreenchange', updateButton);
  };
}

const canvas = document.querySelector<HTMLCanvasElement>('#game-canvas');

if (!canvas) {
  throw new Error('Missing #game-canvas element.');
}

const game = new Game(canvas);
const disposeFullscreenToggle = setupFullscreenToggle();
game.start();

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    disposeFullscreenToggle();
    game.dispose();
  });
}
